import { httpClient } from '@/core/api/httpClient';
import { API_V1 } from '@/core/api/endpoints';
import { env } from '@/core/config/env';
import { toPascalCaseParams } from '@/shared/lib/to-pascal-case-params';
import type { CreatedResponse } from '@/shared/types/createdResponse';
import type {
  TimeOffPolicyDto,
  TimeOffTypeDto,
  SeniorityMatrixEntryDto,
  HolidayDto,
  TimeOffRequestDto,
  TimeOffRequestDtoPagedResult,
  TimeOffBalanceDto,
  TimeOffSummaryDto,
  AbsenceDayDto,
  TimeOffAbsenceSummaryDto,
  MyTimeOffRequestsQueryParams,
  TimeOffDateRangeQueryParams,
  TimeOffTypesQueryParams,
  HolidaysQueryParams,
  AdminUpcomingQueryParams,
  UpdateTimeOffPolicyPayload,
  CreateTimeOffTypePayload,
  UpdateTimeOffTypePayload,
  ReplaceSeniorityMatrixPayload,
  HolidayPayload,
  ReseedHolidaysPayload,
  CreateMemberAdjustmentPayload,
  CreateTimeOffRequestPayload,
  ApproveTimeOffRequestPayload,
  RejectTimeOffRequestPayload,
  OverrideTimeOffRequestPayload,
  CreateHistoricalTimeOffRequestPayload,
  BulkImportHistoricalResultDto,
} from '../types';
import {
  timeOffPolicyMock,
  timeOffTypesMock,
  seniorityMatrixMock,
  holidaysMock,
  myTimeOffRequestsMock,
  timeOffRequestPendingMock,
  timeOffBalanceMock,
  timeOffSummaryMock,
  pendingTimeOffApprovalsMock,
  teamTimeOffAbsencesMock,
  adminTodayMock,
  adminUpcomingMock,
  adminSummaryMock,
  timeOffAttachmentBlobMock,
  bulkImportHistoricalResultMock,
} from './time-off.mock';
import { normalizeTimeOffRequest } from '../adapters/time-off.adapter';

const USE_MOCK = env.VITE_USE_MOCK === 'true';
const BASE = `${API_V1}/time-off`;

export const timeOffService = {
  // ─── Activation ────────────────────────────────────────────────────────────

  /** `POST /time-off/activate` — Owner/Admin only. Idempotent. */
  activate: async (): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.post<undefined, void>(`${BASE}/activate`, undefined);
  },

  // ─── Configuration (Owner/Admin) ────────────────────────────────────────────

  /** `GET /time-off/policy` */
  getPolicy: async (signal?: AbortSignal): Promise<TimeOffPolicyDto> => {
    if (USE_MOCK) return { ...timeOffPolicyMock };
    const { data } = await httpClient.get<TimeOffPolicyDto>(`${BASE}/policy`, { signal });
    return data;
  },

  /** `PUT /time-off/policy` */
  updatePolicy: async (payload: UpdateTimeOffPolicyPayload): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.put<UpdateTimeOffPolicyPayload, void>(`${BASE}/policy`, payload);
  },

  /** `GET /time-off/types?includeInactive` */
  getTypes: async (
    params?: TimeOffTypesQueryParams,
    signal?: AbortSignal,
  ): Promise<TimeOffTypeDto[]> => {
    if (USE_MOCK) {
      return params?.includeInactive
        ? timeOffTypesMock
        : timeOffTypesMock.filter((t) => t.isActive);
    }
    const { data } = await httpClient.get<TimeOffTypeDto[]>(`${BASE}/types`, {
      params: params ? toPascalCaseParams(params as Record<string, unknown>) : undefined,
      signal,
    });
    return data;
  },

  /** `POST /time-off/types` */
  createType: async (payload: CreateTimeOffTypePayload): Promise<CreatedResponse> => {
    if (USE_MOCK) return { id: 'type-new' };
    const { data } = await httpClient.post<CreateTimeOffTypePayload, CreatedResponse>(
      `${BASE}/types`,
      payload,
    );
    return data;
  },

  /** `PUT /time-off/types/{typeId}` */
  updateType: async (typeId: string, payload: UpdateTimeOffTypePayload): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.put<UpdateTimeOffTypePayload, void>(`${BASE}/types/${typeId}`, payload);
  },

  /** `DELETE /time-off/types/{typeId}` — soft-deactivates. */
  deleteType: async (typeId: string): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.delete(`${BASE}/types/${typeId}`);
  },

  /** `GET /time-off/seniority-matrix` */
  getSeniorityMatrix: async (signal?: AbortSignal): Promise<SeniorityMatrixEntryDto[]> => {
    if (USE_MOCK) return [...seniorityMatrixMock];
    const { data } = await httpClient.get<SeniorityMatrixEntryDto[]>(`${BASE}/seniority-matrix`, {
      signal,
    });
    return data;
  },

  /** `PUT /time-off/seniority-matrix` — full replace. */
  updateSeniorityMatrix: async (payload: ReplaceSeniorityMatrixPayload): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.put<ReplaceSeniorityMatrixPayload, void>(`${BASE}/seniority-matrix`, payload);
  },

  /** `GET /time-off/holidays?year` */
  getHolidays: async (params: HolidaysQueryParams, signal?: AbortSignal): Promise<HolidayDto[]> => {
    if (USE_MOCK) return holidaysMock.filter((h) => h.date.startsWith(String(params.year)));
    const { data } = await httpClient.get<HolidayDto[]>(`${BASE}/holidays`, {
      params: toPascalCaseParams(params as unknown as Record<string, unknown>),
      signal,
    });
    return data;
  },

  /** `POST /time-off/holidays` */
  createHoliday: async (payload: HolidayPayload): Promise<CreatedResponse> => {
    if (USE_MOCK) return { id: 'holiday-new' };
    const { data } = await httpClient.post<HolidayPayload, CreatedResponse>(
      `${BASE}/holidays`,
      payload,
    );
    return data;
  },

  /** `PUT /time-off/holidays/{holidayId}` */
  updateHoliday: async (holidayId: string, payload: HolidayPayload): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.put<HolidayPayload, void>(`${BASE}/holidays/${holidayId}`, payload);
  },

  /** `DELETE /time-off/holidays/{holidayId}` */
  deleteHoliday: async (holidayId: string): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.delete(`${BASE}/holidays/${holidayId}`);
  },

  /** `POST /time-off/holidays/reseed` — regenerates official holidays for a year only. */
  reseedHolidays: async (payload: ReseedHolidaysPayload): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.post<ReseedHolidaysPayload, void>(`${BASE}/holidays/reseed`, payload);
  },

  // ─── People administration (Owner/Admin) ────────────────────────────────────

  /**
   * `POST /time-off/members/{userId}/cycles` — manually opens an entitlement
   * cycle. Omitting `cycleNumber` opens the member's current cycle (unchanged
   * behavior); passing a `cycleNumber` below the member's current one opens a
   * historical, immediately-`Closed` cycle instead.
   */
  openMemberCycle: async (userId: string, cycleNumber?: number): Promise<CreatedResponse> => {
    if (USE_MOCK) return { id: 'cycle-new' };
    const url = `${BASE}/members/${userId}/cycles`;
    const { data } =
      cycleNumber !== undefined
        ? await httpClient.post<undefined, CreatedResponse>(url, undefined, {
            params: { cycleNumber },
          })
        : await httpClient.post<undefined, CreatedResponse>(url, undefined);
    return data;
  },

  /** `POST /time-off/members/{userId}/adjustments` — `days` may be negative. */
  createMemberAdjustment: async (
    userId: string,
    payload: CreateMemberAdjustmentPayload,
  ): Promise<CreatedResponse> => {
    if (USE_MOCK) return { id: 'adjustment-new' };
    const { data } = await httpClient.post<CreateMemberAdjustmentPayload, CreatedResponse>(
      `${BASE}/members/${userId}/adjustments`,
      payload,
    );
    return data;
  },

  // ─── Historical load (Owner/Admin) ──────────────────────────────────────────

  /**
   * `POST /time-off/admin/historical-requests` — registers a single past-dated
   * request on behalf of another member. Resolved as `Approved` immediately,
   * no notification sent.
   */
  createHistoricalRequest: async (
    payload: CreateHistoricalTimeOffRequestPayload,
  ): Promise<CreatedResponse> => {
    if (USE_MOCK) return { id: 'historical-request-new' };
    const { data } = await httpClient.post<CreateHistoricalTimeOffRequestPayload, CreatedResponse>(
      `${BASE}/admin/historical-requests`,
      payload,
    );
    return data;
  },

  /**
   * `POST /time-off/admin/historical-requests/bulk` — imports historical
   * requests from a CSV/Excel file, parsed server-side. Each row is one day;
   * rows sharing `user_email+type_code+cycle_number+reason` are grouped into
   * one request. Always returns `200` with one error entry per failing row.
   */
  bulkImportHistoricalRequests: async (file: File): Promise<BulkImportHistoricalResultDto> => {
    if (USE_MOCK) return { ...bulkImportHistoricalResultMock };
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await httpClient.post<FormData, BulkImportHistoricalResultDto>(
      `${BASE}/admin/historical-requests/bulk`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  // ─── Requests ────────────────────────────────────────────────────────────────

  /** `POST /time-off/requests` — returns the full chain in `steps[]`. */
  createRequest: async (payload: CreateTimeOffRequestPayload): Promise<TimeOffRequestDto> => {
    if (USE_MOCK) return { ...timeOffRequestPendingMock };
    const { data } = await httpClient.post<CreateTimeOffRequestPayload, TimeOffRequestDto>(
      `${BASE}/requests`,
      payload,
    );
    return normalizeTimeOffRequest(data);
  },

  /** `GET /time-off/requests/{requestId}` — redacted per the caller's visibility level. */
  getRequest: async (requestId: string, signal?: AbortSignal): Promise<TimeOffRequestDto> => {
    if (USE_MOCK) return { ...timeOffRequestPendingMock, id: requestId };
    const { data } = await httpClient.get<TimeOffRequestDto>(`${BASE}/requests/${requestId}`, {
      signal,
    });
    return normalizeTimeOffRequest(data);
  },

  /** `POST /time-off/requests/{requestId}/approve` — current step's approver only. */
  approveRequest: async (
    requestId: string,
    payload: ApproveTimeOffRequestPayload,
  ): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.post<ApproveTimeOffRequestPayload, void>(
      `${BASE}/requests/${requestId}/approve`,
      payload,
    );
  },

  /** `POST /time-off/requests/{requestId}/reject` — current step's approver only. */
  rejectRequest: async (requestId: string, payload: RejectTimeOffRequestPayload): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.post<RejectTimeOffRequestPayload, void>(
      `${BASE}/requests/${requestId}/reject`,
      payload,
    );
  },

  /** `POST /time-off/requests/{requestId}/override` — Owner/Admin only, bypasses the chain. */
  overrideRequest: async (
    requestId: string,
    payload: OverrideTimeOffRequestPayload,
  ): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.post<OverrideTimeOffRequestPayload, void>(
      `${BASE}/requests/${requestId}/override`,
      payload,
    );
  },

  /** `DELETE /time-off/requests/{requestId}` — requester only, cancels the entire request. */
  cancelRequest: async (requestId: string): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.delete(`${BASE}/requests/${requestId}`);
  },

  /** `DELETE /time-off/requests/{requestId}/days/{date}` — requester only, partial cancellation. */
  cancelRequestDay: async (requestId: string, date: string): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.delete(`${BASE}/requests/${requestId}/days/${date}`);
  },

  /** `POST /time-off/requests/{requestId}/attachments` — multipart, requester only. */
  uploadAttachment: async (requestId: string, file: File): Promise<CreatedResponse> => {
    if (USE_MOCK) return { id: 'attachment-new' };
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await httpClient.post<FormData, CreatedResponse>(
      `${BASE}/requests/${requestId}/attachments`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  /**
   * `GET /time-off/requests/{requestId}/attachments/{attachmentId}` — requires `Full`
   * visibility; anything less returns `404` even if the request metadata is visible.
   */
  downloadAttachment: async (requestId: string, attachmentId: string): Promise<Blob> => {
    if (USE_MOCK) return timeOffAttachmentBlobMock;
    const { data } = await httpClient.get<Blob>(
      `${BASE}/requests/${requestId}/attachments/${attachmentId}`,
      { responseType: 'blob' },
    );
    return data;
  },

  // ─── Collaborator reads (self) ───────────────────────────────────────────────

  /** `GET /time-off/me/requests?status&page&pageSize` — the only paginated list in this module. */
  getMyRequests: async (
    params?: MyTimeOffRequestsQueryParams,
    signal?: AbortSignal,
  ): Promise<TimeOffRequestDtoPagedResult> => {
    if (USE_MOCK) {
      const items = params?.status
        ? myTimeOffRequestsMock.filter((r) => r.status === params.status)
        : myTimeOffRequestsMock;
      return {
        items,
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 10,
        totalCount: items.length,
        totalPages: 1,
        hasPreviousPage: false,
        hasNextPage: false,
        firstItemOnPage: items.length ? 1 : 0,
        lastItemOnPage: items.length,
      };
    }
    const { data } = await httpClient.get<TimeOffRequestDtoPagedResult>(`${BASE}/me/requests`, {
      params: params ? toPascalCaseParams(params as Record<string, unknown>) : undefined,
      signal,
    });
    return { ...data, items: data.items?.map(normalizeTimeOffRequest) ?? data.items };
  },

  /** `GET /time-off/me/balance` */
  getMyBalance: async (signal?: AbortSignal): Promise<TimeOffBalanceDto> => {
    if (USE_MOCK) return { ...timeOffBalanceMock };
    const { data } = await httpClient.get<TimeOffBalanceDto>(`${BASE}/me/balance`, { signal });
    return data;
  },

  /** `GET /time-off/me/summary` */
  getMySummary: async (signal?: AbortSignal): Promise<TimeOffSummaryDto> => {
    if (USE_MOCK) return { ...timeOffSummaryMock };
    const { data } = await httpClient.get<TimeOffSummaryDto>(`${BASE}/me/summary`, { signal });
    return data;
  },

  /** `GET /time-off/approvals` — requests pending the caller's approval. Plain array, no pagination. */
  getPendingApprovals: async (signal?: AbortSignal): Promise<TimeOffRequestDto[]> => {
    if (USE_MOCK) return [...pendingTimeOffApprovalsMock];
    const { data } = await httpClient.get<TimeOffRequestDto[]>(`${BASE}/approvals`, { signal });
    return data.map(normalizeTimeOffRequest);
  },

  // ─── Team / Admin reads ──────────────────────────────────────────────────────

  /** `GET /time-off/team?from&to` — scoped by visibility, not role. Plain array. */
  getTeamAbsences: async (
    params: TimeOffDateRangeQueryParams,
    signal?: AbortSignal,
  ): Promise<TimeOffRequestDto[]> => {
    if (USE_MOCK) return [...teamTimeOffAbsencesMock];
    const { data } = await httpClient.get<TimeOffRequestDto[]>(`${BASE}/team`, {
      params: toPascalCaseParams(params as unknown as Record<string, unknown>),
      signal,
    });
    return data.map(normalizeTimeOffRequest);
  },

  /** `GET /time-off/admin/today` — Owner/Admin only. Plain array. */
  getAdminToday: async (signal?: AbortSignal): Promise<AbsenceDayDto[]> => {
    if (USE_MOCK) return [...adminTodayMock];
    const { data } = await httpClient.get<AbsenceDayDto[]>(`${BASE}/admin/today`, { signal });
    return data;
  },

  /** `GET /time-off/admin/upcoming?days` — Owner/Admin only, 1-90 days (default 7). Plain array. */
  getAdminUpcoming: async (
    params?: AdminUpcomingQueryParams,
    signal?: AbortSignal,
  ): Promise<AbsenceDayDto[]> => {
    if (USE_MOCK) return [...adminUpcomingMock];
    const { data } = await httpClient.get<AbsenceDayDto[]>(`${BASE}/admin/upcoming`, {
      params: params ? toPascalCaseParams(params as Record<string, unknown>) : undefined,
      signal,
    });
    return data;
  },

  /** `GET /time-off/admin/summary?from&to` — Owner/Admin only. */
  getAdminSummary: async (
    params: TimeOffDateRangeQueryParams,
    signal?: AbortSignal,
  ): Promise<TimeOffAbsenceSummaryDto> => {
    if (USE_MOCK) return { ...adminSummaryMock };
    const { data } = await httpClient.get<TimeOffAbsenceSummaryDto>(`${BASE}/admin/summary`, {
      params: toPascalCaseParams(params as unknown as Record<string, unknown>),
      signal,
    });
    return data;
  },
};
